/**
 * The **pilot-gear pick affordance** the PILOT sheet's EDIT mode
 * (`PilotSheetEdit.tsx`) renders — a thin display layer over the SAME
 * single-source helpers the reducer wall uses (`canAddPilotGear` /
 * `pilotGearCounts` / `PILOT_GEAR_LIMITS` / `pilotGearCategory` in `pilotGear.ts` from
 * `@lancer-vtt/domain`). So the grey-out can never drift from the wall: the reducer
 * (`add_pilot_gear`) still hard-rejects an add that busts the per-category limit
 * (2 weapons / 1 armor / 3 gear, RAW Core p. 34); this just makes the player see *which
 * category is full* before clicking.
 *
 * **Mirror, never re-derive (the core invariant).** The 2/1/3 caps live ONCE in
 * `@lancer-vtt/domain` (read off `rules.json`); this module imports and calls
 * `canAddPilotGear` / `pilotGearCounts` / `pilotGearCategory`, it never re-implements the
 * limits. The category an item counts against is **derived off the compendium item's
 * `type`, never stored**, exactly as the reducer derives it at gate time.
 *
 * Three row states, mirroring the reducer's single gate (no LL/license gate — pilot gear
 * is free per RAW):
 *   - `held` — already in the loadout.
 *   - `available` — not held, and an add fits the category's remaining room.
 *   - `locked` — not held, but the item's category is already at its limit (the reason
 *     names which category is full).
 */
import {
  PILOT_GEAR_LIMITS,
  pilotGearCategory,
  pilotGearCounts,
  canAddPilotGear,
  type PilotData,
  type PilotGearCategory,
} from "@lancer-vtt/domain";
import type { Compendium } from "@lancer-vtt/compendium";

export type PilotGearStatus =
  | { state: "held" }
  | { state: "available" }
  | { state: "locked"; reason: string };

/** A locked row carries a reason; the pure states don't. */
export type PilotGearRow = {
  id: string;
  name: string;
  category: PilotGearCategory;
  description: string | undefined;
  status: PilotGearStatus;
};

/** A per-category header tally the leaf shows. */
export type PilotGearCategoryTally = {
  category: PilotGearCategory;
  used: number;
  limit: number;
  atLimit: boolean;
};

/** The per-category tallies plus a row per compendium pilot-gear item. */
export type PilotGearAffordance = {
  categories: PilotGearCategoryTally[];
  rows: PilotGearRow[];
};

/** Display order + label for the three categories. */
const CATEGORY_ORDER: PilotGearCategory[] = ["weapon", "armor", "gear"];
const CATEGORY_LABEL: Record<PilotGearCategory, string> = {
  weapon: "Weapon",
  armor: "Armor",
  gear: "Gear",
};
// "Weapons" / "Armor" / "Gear" — armor and gear read as mass nouns, so only weapon pluralizes.
const CATEGORY_LABEL_PLURAL: Record<PilotGearCategory, string> = {
  weapon: "Weapons",
  armor: "Armor",
  gear: "Gear",
};

/** The label a category renders under (capitalized, singular). */
export function pilotGearCategoryLabel(category: PilotGearCategory): string {
  return CATEGORY_LABEL[category];
}

/** "Weapons full — 2/2 carried" — the reason a `locked` row carries. */
function lockReason(category: PilotGearCategory, limit: number): string {
  return `${CATEGORY_LABEL_PLURAL[category]} full — ${limit}/${limit} carried`;
}

/**
 * The per-item pick affordance for the whole pilot-gear catalog, grouped category-then-name.
 * Mirrors `add_pilot_gear` exactly (the per-category limit is the only gate), so a row the
 * leaf greys is precisely the take the reducer would reject.
 */
export function pilotGearAffordance(pilot: PilotData, compendium: Compendium): PilotGearAffordance {
  const counts = pilotGearCounts(pilot, compendium);

  const categories: PilotGearCategoryTally[] = CATEGORY_ORDER.map((category) => ({
    category,
    used: counts[category],
    limit: PILOT_GEAR_LIMITS[category],
    atLimit: counts[category] >= PILOT_GEAR_LIMITS[category],
  }));

  const rows: PilotGearRow[] = compendium
    .list("pilot_gear")
    .slice()
    .sort((a, b) => {
      const ca = CATEGORY_ORDER.indexOf(pilotGearCategory(a));
      const cb = CATEGORY_ORDER.indexOf(pilotGearCategory(b));
      return ca - cb || (a.name ?? "").localeCompare(b.name ?? "");
    })
    .map((item) => {
      const category = pilotGearCategory(item);
      const base = { id: item.id, name: item.name ?? item.id, category, description: item.description };
      if (pilot.gear.includes(item.id)) {
        return { ...base, status: { state: "held" as const } };
      }
      if (canAddPilotGear({ pilot, item, compendium })) {
        return { ...base, status: { state: "available" as const } };
      }
      return {
        ...base,
        status: { state: "locked" as const, reason: lockReason(category, PILOT_GEAR_LIMITS[category]) },
      };
    });

  return { categories, rows };
}
